import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ROLES, type Role } from '@/types/auth'
import {
	NOTICE_PRIORITIES,
	NOTICE_TYPES,
	type NoticeMedia,
	type NoticePriority,
	type NoticeStatus,
	type NoticeType,
} from '@/types/notice'
import type { OrgBranch } from '@/types/organization'
import { formatDateYMD } from '@/utils/formatDate'
import { Spinner } from '../ui/spinner'
import { BranchSelector } from './branch-selector'
import {
	NoticeMediaPicker,
	type NoticeMediaSelection,
} from './notice-media-picker'

export interface Notification {
	id: string
	branchIds: string[]
	roles: Role[]
	title: string
	message: string
	priority: NoticePriority
	type: NoticeType
	startsAt: string | null
	endsAt: string | null
	media: NoticeMedia[]
	status: NoticeStatus
	isActive: boolean
	isGlobal: boolean
}

export interface NotificationFormData {
	branchIds?: string[]
	allRoles: boolean
	roles?: Role[]
	title: string
	message: string
	priority: NoticePriority
	type: NoticeType
	/** yyyy-MM-dd; string vazia limpa a data */
	startsAt: string
	endsAt: string
	isActive: boolean
	isGlobal: boolean
	mediaFiles: File[]
	/** Só na edição: mídias já gravadas que continuam no aviso */
	keepMediaIds?: string[]
}

export interface NotificationFormProps {
	mode: 'create' | 'edit'
	initial?: Notification
	branches: OrgBranch[]
	onSave: (
		data: NotificationFormData,
		onProgress: (percent: number) => void,
	) => void | Promise<void>
	onClose: () => void
}

// A vigência é gravada no fuso local (início 00:00, fim 23:59:59), então a data exibida deve
// ser lida em hora local — `iso.slice(0, 10)` mostraria o dia seguinte para o fim.
const isoToDateValue = (iso: string | null | undefined): string =>
	iso ? formatDateYMD(new Date(iso)) : ''

export function NotificationFormContent({
	mode,
	initial,
	branches,
	onSave,
	onClose,
}: NotificationFormProps) {
	const { t } = useTranslation()
	const [disableSaving, setDisableSaving] = useState(false)
	const [title, setTitle] = useState(initial?.title ?? '')
	const [message, setMessage] = useState(initial?.message ?? '')
	const [priority, setPriority] = useState<NoticePriority>(
		initial?.priority ?? 'medium',
	)
	const [type, setType] = useState<NoticeType>(initial?.type ?? 'info')
	const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true)
	const [isGlobal, setIsGlobal] = useState<boolean>(initial?.isGlobal ?? false)
	const [startsAt, setStartsAt] = useState(isoToDateValue(initial?.startsAt))
	const [endsAt, setEndsAt] = useState(isoToDateValue(initial?.endsAt))
	const [selectedBranches, setSelectedBranches] = useState<string[]>(
		initial?.branchIds ?? [],
	)
	const [allRoles, setAllRoles] = useState<boolean>(
		(initial?.roles.length ?? 0) === 0,
	)
	const [selectedRoles, setSelectedRoles] = useState<Role[]>(
		initial?.roles ?? [],
	)
	const [media, setMedia] = useState<NoticeMediaSelection>({
		existing: initial?.media ?? [],
		files: [],
	})
	const [uploadProgress, setUploadProgress] = useState<number | null>(null)

	const toggleRole = (role: Role) =>
		setSelectedRoles((current) =>
			current.includes(role)
				? current.filter((item) => item !== role)
				: [...current, role],
		)

	const handleSave = async () => {
		if (disableSaving) {
			return
		}

		if (!title.trim()) {
			toast.error(t('administration.notificationsForm.emptyTitleError'))
			return
		}
		if (!message.trim()) {
			toast.error(t('administration.notificationsForm.emptyMessageError'))
			return
		}
		if (!isGlobal && selectedBranches.length === 0) {
			toast.error(t('administration.notificationsForm.emptyBranchError'))
			return
		}
		if (!allRoles && selectedRoles.length === 0) {
			toast.error(t('administration.notificationsForm.emptyRolesError'))
			return
		}
		if (startsAt && endsAt && endsAt < startsAt) {
			toast.error(t('administration.notificationsForm.dateRangeError'))
			return
		}

		setDisableSaving(true)
		try {
			await Promise.resolve(
				onSave(
					{
						branchIds: isGlobal ? undefined : selectedBranches,
						allRoles,
						roles: allRoles ? undefined : selectedRoles,
						title: title.trim(),
						message: message.trim(),
						priority,
						type,
						startsAt,
						endsAt,
						isActive,
						isGlobal,
						mediaFiles: media.files,
						keepMediaIds: initial
							? media.existing.map((item) => item.id)
							: undefined,
					},
					media.files.length > 0 ? setUploadProgress : () => {},
				),
			)
		} catch (error) {
			toast.error(`Erro ao salvar ${String(error)}`)
		} finally {
			setDisableSaving(false)
			setUploadProgress(null)
		}
	}

	return (
		<div className="flex flex-col gap-5">
			{/* Title */}
			<Field>
				<FieldLabel htmlFor="notification-title">
					{t('administration.notificationsForm.titleLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="notification-title"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder={t('administration.notificationsForm.titlePlaceholder')}
					/>
				</FieldContent>
			</Field>

			{/* Message */}
			<Field>
				<FieldLabel htmlFor="notification-message">
					{t('administration.notificationsForm.messageLabel')}
				</FieldLabel>
				<FieldContent>
					<Textarea
						id="notification-message"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						placeholder={t(
							'administration.notificationsForm.messagePlaceholder',
						)}
						rows={4}
						className="resize-none"
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			{/* Priority & Type row */}
			<div className="grid grid-cols-2 gap-4">
				<Field>
					<FieldLabel htmlFor="notification-priority">
						{t('administration.notificationsForm.priorityLabel')}
					</FieldLabel>
					<FieldContent>
						<NativeSelect
							id="notification-priority"
							value={priority}
							onChange={(e) => setPriority(e.target.value as NoticePriority)}
						>
							{NOTICE_PRIORITIES.map((p) => (
								<NativeSelectOption key={p} value={p}>
									{t(`administration.notificationsForm.priority_${p}`)}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</FieldContent>
				</Field>

				<Field>
					<FieldLabel htmlFor="notification-type">
						{t('administration.notificationsForm.typeLabel')}
					</FieldLabel>
					<FieldContent>
						<NativeSelect
							id="notification-type"
							value={type}
							onChange={(e) => setType(e.target.value as NoticeType)}
						>
							{NOTICE_TYPES.map((tp) => (
								<NativeSelectOption key={tp} value={tp}>
									{t(`administration.notificationsForm.type_${tp}`)}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</FieldContent>
				</Field>
			</div>

			{/* Dates row */}
			<div className="grid grid-cols-2 gap-4">
				<Field>
					<FieldLabel htmlFor="notification-starts-at">
						{t('administration.notificationsForm.startsAtLabel')}
					</FieldLabel>
					<FieldContent>
						<Input
							id="notification-starts-at"
							type="date"
							value={startsAt}
							max={endsAt || undefined}
							onChange={(e) => setStartsAt(e.target.value)}
						/>
					</FieldContent>
				</Field>
				<Field>
					<FieldLabel htmlFor="notification-ends-at">
						{t('administration.notificationsForm.endsAtLabel')}
					</FieldLabel>
					<FieldContent>
						<Input
							id="notification-ends-at"
							type="date"
							value={endsAt}
							min={startsAt || undefined}
							onChange={(e) => setEndsAt(e.target.value)}
						/>
					</FieldContent>
				</Field>
			</div>

			{/* Media */}
			<Field>
				<FieldLabel htmlFor="notification-media">
					{t('administration.notificationsForm.mediaLabel')}
				</FieldLabel>
				<FieldContent>
					<NoticeMediaPicker
						value={media}
						onChange={setMedia}
						uploadProgress={uploadProgress}
					/>
				</FieldContent>
			</Field>

			{/* Flags row */}
			<div className="flex flex-col gap-2">
				<label className="flex cursor-pointer items-center gap-3">
					<input
						id="notification-global"
						type="checkbox"
						checked={isGlobal}
						onChange={(e) => setIsGlobal(e.target.checked)}
						className="size-4 cursor-pointer accent-kaefer-medium-grey"
					/>
					<span className="text-sm font-medium">
						{t('administration.notificationsForm.isGlobalLabel')}
					</span>
				</label>
				<label className="flex cursor-pointer items-center gap-3">
					<input
						id="notification-active"
						type="checkbox"
						checked={isActive}
						onChange={(e) => setIsActive(e.target.checked)}
						className="size-4 cursor-pointer accent-kaefer-medium-grey"
					/>
					<span className="text-sm font-medium">
						{t('administration.notificationsForm.isActiveLabel')}
					</span>
				</label>
			</div>

			{/* Branch selector — hidden when isGlobal */}
			{!isGlobal && (
				<BranchSelector
					branches={branches}
					selected={selectedBranches}
					onChange={setSelectedBranches}
					title={t('administration.notificationsForm.branchesTitle')}
				/>
			)}

			{/* Roles — combina com a filial: recebe quem é da filial E de um dos perfis */}
			<Field>
				<FieldLabel>
					{t('administration.notificationsForm.rolesTitle')}
				</FieldLabel>
				<FieldContent>
					<label className="flex cursor-pointer items-center gap-3">
						<input
							id="notification-all-roles"
							type="checkbox"
							checked={allRoles}
							onChange={(e) => setAllRoles(e.target.checked)}
							className="size-4 cursor-pointer accent-kaefer-medium-grey"
						/>
						<span className="text-sm font-medium">
							{t('administration.notificationsForm.allRolesLabel')}
						</span>
					</label>
					{!allRoles && (
						<div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
							{ROLES.map((role) => (
								<label
									key={role}
									className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2"
								>
									<input
										type="checkbox"
										checked={selectedRoles.includes(role)}
										onChange={() => toggleRole(role)}
										className="size-4 cursor-pointer accent-kaefer-medium-grey"
									/>
									<span className="text-sm">{t(`roles.${role}`)}</span>
								</label>
							))}
						</div>
					)}
					<p className="mt-2 text-muted-foreground text-xs">
						{t('administration.notificationsForm.rolesHint')}
					</p>
				</FieldContent>
			</Field>

			{/* Footer buttons */}
			<div className="mt-auto flex gap-3 pt-1 sticky bottom-0 bg-background py-2">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.notificationsForm.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={disableSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create' ? (
						disableSaving ? (
							<>
								<Spinner /> {t('administration.classificationsForm.saving')}
							</>
						) : (
							t('administration.notificationsForm.saveButton')
						)
					) : disableSaving ? (
						<>
							<Spinner /> {t('administration.classificationsForm.saving')}
						</>
					) : (
						t('administration.notifications.saveEdit')
					)}
				</Button>
			</div>
		</div>
	)
}
